/**
 * F-404-FE-01: NoteEditorPage
 *
 * Full-screen note editor with JSON-driven form matching template field structure.
 * - 30-second auto-save interval via setInterval → PATCH /api/notes/:id/autosave
 * - Navigation guard (beforeunload + React Router blocker) to trigger auto-save on exit
 * - "Saved [time]" / "Saving..." indicator in top bar
 * - Transcript slide-in side panel toggle
 * - DRAFT/COMPLETING status badge
 * - ScrollGateButton at the bottom
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { api, ApiError } from '@/lib/apiClient';
import { ScrollGateButton } from './ScrollGateButton';
import type { Note, TemplateField, TranscriptSegment } from './types';
import { useCopyToEmr } from './useCopyToEmr';
import { useNotePreview } from './useNotePreview';

// ---- Helpers ---------------------------------------------------------------

// Debounce window between the last keystroke and the coordinated
// "save then refresh preview" action. Long enough to coalesce normal
// typing, short enough that the doctor sees the preview catch up
// quickly after they pause.
const SAVE_REFRESH_DEBOUNCE_MS = 300;

function formatRelativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ---- Field Renderer --------------------------------------------------------

interface FieldRendererProps {
  field: TemplateField;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  disabled?: boolean;
}

function FieldRenderer({ field, value, onChange, disabled }: FieldRendererProps) {
  // Defensive: if value is an object (e.g., unwrapped note content), stringify it for display.
  // This prevents [object Object] rendering when backend unwrapping doesn't catch all patterns.
  const strValue = value === null || value === undefined
    ? ''
    : typeof value === 'object'
      ? JSON.stringify(value, null, 2)
      : String(value);
  const boolValue = typeof value === 'boolean' ? value : false;

  const labelEl = (
    <label htmlFor={`field-${field.key}`} className="form-label">
      {field.label}
      {field.required && (
        <span className="text-safety-red ms-1" aria-label="required">
          *
        </span>
      )}
    </label>
  );

  if (field.type === 'textarea') {
    return (
      <div>
        {labelEl}
        <textarea
          id={`field-${field.key}`}
          className="form-textarea"
          value={strValue}
          placeholder={field.placeholder ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(field.key, e.target.value)}
          rows={5}
        />
      </div>
    );
  }

  if (field.type === 'select' && field.options) {
    return (
      <div>
        {labelEl}
        <select
          id={`field-${field.key}`}
          className="form-input"
          value={strValue}
          disabled={disabled}
          onChange={(e) => onChange(field.key, e.target.value)}
        >
          <option value="">Select...</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div className="flex items-center gap-3">
        <input
          id={`field-${field.key}`}
          type="checkbox"
          className="h-5 w-5 rounded border-clinical-border text-clinical-teal focus:ring-clinical-amber"
          checked={boolValue}
          disabled={disabled}
          onChange={(e) => onChange(field.key, e.target.checked)}
        />
        <label htmlFor={`field-${field.key}`} className="form-label mb-0">
          {field.label}
          {field.required && (
            <span className="text-safety-red ms-1">*</span>
          )}
        </label>
      </div>
    );
  }

  // text / number (default)
  return (
    <div>
      {labelEl}
      <input
        id={`field-${field.key}`}
        type={field.type === 'number' ? 'number' : 'text'}
        className="form-input"
        value={strValue}
        placeholder={field.placeholder ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const v =
            field.type === 'number'
              ? e.target.value === ''
                ? ''
                : Number(e.target.value)
              : e.target.value;
          onChange(field.key, v);
        }}
      />
    </div>
  );
}

// ---- Transcript Panel -------------------------------------------------------

interface TranscriptPanelProps {
  segments: TranscriptSegment[];
  onClose: () => void;
}

function TranscriptPanel({ segments, onClose }: TranscriptPanelProps) {
  return (
    <div
      className={[
        'fixed inset-y-0 end-0 z-50 w-full max-w-sm',
        'bg-white border-s border-clinical-border shadow-clinical-lg',
        'flex flex-col',
        'transition-transform duration-300 ease-out',
      ].join(' ')}
      role="complementary"
      aria-label="Session transcript"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-clinical-border">
        <h2 className="font-heading text-base font-semibold text-clinical-charcoal">
          Session Transcript
        </h2>
        <button
          type="button"
          className="p-2 rounded-lg text-clinical-text-muted hover:text-clinical-charcoal hover:bg-clinical-cream transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          onClick={onClose}
          aria-label="Close transcript panel"
        >
          <XIcon />
        </button>
      </div>

      {/* Segments */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {segments.length === 0 ? (
          <p className="text-sm text-clinical-text-muted text-center py-8 font-body">
            No transcript available.
          </p>
        ) : (
          segments.map((seg) => (
            <div
              key={seg.id}
              className={[
                'rounded-clinical p-3 text-sm font-body',
                seg.speaker === 'DOCTOR'
                  ? 'bg-clinical-cream border border-clinical-border ms-4'
                  : 'bg-clinical-teal/5 border border-clinical-teal/20 me-4',
              ].join(' ')}
            >
              <p className="text-xs font-semibold text-clinical-text-muted mb-1 uppercase tracking-wide">
                {seg.speaker === 'DOCTOR' ? 'Doctor' : 'Patient'}
              </p>
              <p className="text-clinical-charcoal-soft leading-relaxed">
                {seg.originalText}
              </p>
              {seg.translatedText && seg.translatedText !== seg.originalText && (
                <p className="text-clinical-text-muted mt-1 italic leading-relaxed">
                  {seg.translatedText}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ---- Main Page Component ---------------------------------------------------

export function NoteEditorPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();

  // Data state
  const [note, setNote] = useState<Note | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);

  // Editor state
  const [content, setContent] = useState<Record<string, unknown>>({});
  const [isDirty, setIsDirty] = useState(false);
  const contentRef = useRef<Record<string, unknown>>({});
  const noteIdRef = useRef<string | undefined>(noteId);
  // isDirtyRef mirrors isDirty for use inside callbacks scheduled via
  // setTimeout (handleFieldChange's debounce, navigation/beforeunload
  // guards). Reading the state directly there would capture a stale
  // closure and skip the autosave PATCH the preview depends on.
  const isDirtyRef = useRef(false);

  // Save indicator state
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [lastSavedDisplay, setLastSavedDisplay] = useState<string>('');
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const relativeTimeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const saveAndRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // UI state
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptOverlay, setTranscriptOverlay] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { state: copyState, copy: copyToEmr } = useCopyToEmr();

  // Preview pane state
  const [previewOn, setPreviewOn] = useState(false);
  const { markdown: previewMarkdown, loading: previewLoading, refresh: refreshPreview } = useNotePreview(noteId ?? '', previewOn);

  // Keep refs in sync for use in callbacks
  useEffect(() => {
    contentRef.current = content;
  }, [content]);
  useEffect(() => {
    noteIdRef.current = noteId;
  }, [noteId]);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);
  // Cleanup the coordinated save+refresh timer on unmount.
  useEffect(() => {
    return () => {
      if (saveAndRefreshTimerRef.current !== null) {
        clearTimeout(saveAndRefreshTimerRef.current);
      }
    };
  }, []);

  // Load note data
  useEffect(() => {
    if (!noteId) return;
    let cancelled = false;

    async function load() {
      try {
        const { data } = await api.get<{ data: Note }>(`/notes/${noteId}`);
        if (cancelled) return;
        setNote(data);
        const initialContent = data.content ?? {};
        setContent(initialContent);
        contentRef.current = initialContent;

        // Also fetch transcript segments
        const segData = await api
          .get<{ segments: TranscriptSegment[] }>(`/sessions/${data.sessionId}/transcript`)
          .catch(() => ({ segments: [] }));
        if (!cancelled) setSegments(segData.segments);
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError ? err.message : 'Failed to load note.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [noteId]);

  // Auto-save function. Reads isDirtyRef (not the isDirty state) so the
  // closure stays current when called from the typing-debounce timer
  // and the 30 s interval; that means deps can be empty and the
  // identity is stable across renders.
  const performSave = useCallback(async (): Promise<void> => {
    const id = noteIdRef.current;
    if (!id || !isDirtyRef.current) return;
    setSaveState('saving');
    try {
      await api.patch(`/notes/${id}/autosave`, {
        content: contentRef.current,
      });
      const now = new Date();
      setLastSavedAt(now);
      setLastSavedDisplay(formatRelativeTime(now));
      setSaveState('saved');
      setIsDirty(false);
    } catch {
      setSaveState('error');
    }
  }, []);

  // 30-second auto-save interval
  useEffect(() => {
    saveTimerRef.current = setInterval(() => {
      void performSave();
    }, 30_000);
    return () => {
      if (saveTimerRef.current !== null) {
        clearInterval(saveTimerRef.current);
      }
    };
  }, [performSave]);

  // Update relative time display every 30s
  useEffect(() => {
    relativeTimeTimerRef.current = setInterval(() => {
      if (lastSavedAt) {
        setLastSavedDisplay(formatRelativeTime(lastSavedAt));
      }
    }, 30_000);
    return () => {
      if (relativeTimeTimerRef.current !== null) {
        clearInterval(relativeTimeTimerRef.current);
      }
    };
  }, [lastSavedAt]);

  // beforeunload guard
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (isDirty) {
        e.preventDefault();
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Navigation guard — auto-save when navigating away with unsaved changes.
  // Uses location change detection instead of useBlocker (which requires createBrowserRouter).
  const location = useLocation();
  const prevLocationRef = useRef(location);
  useEffect(() => {
    if (prevLocationRef.current !== location && isDirty) {
      void performSave();
    }
    prevLocationRef.current = location;
  }, [location, isDirty, performSave]);

  // Field change handler. After the typing pause, flush the autosave
  // PATCH *before* refreshing the preview — otherwise the 300 ms
  // preview debounce can fire before the 30 s setInterval autosave,
  // and the renderer reads stale note.content from the DB.
  const handleFieldChange = useCallback((key: string, value: unknown) => {
    setContent((prev) => {
      const next = { ...prev, [key]: value };
      contentRef.current = next;
      return next;
    });
    setIsDirty(true);
    setSaveState('idle');

    if (saveAndRefreshTimerRef.current !== null) {
      clearTimeout(saveAndRefreshTimerRef.current);
    }
    saveAndRefreshTimerRef.current = setTimeout(async () => {
      await performSave();
      refreshPreview();
    }, SAVE_REFRESH_DEBOUNCE_MS);
  }, [performSave, refreshPreview]);

  // After completion: navigate to completed notes list
  const handleComplete = useCallback(() => {
    setNote((prev) => (prev ? { ...prev, status: 'COMPLETED' } : prev));
    void navigate('/notes/completed');
  }, [navigate]);

  // Transcript panel — animate open/close
  const openTranscript = useCallback(() => {
    setTranscriptOverlay(true);
    // Slight delay so CSS transition fires
    requestAnimationFrame(() => setShowTranscript(true));
  }, []);

  const closeTranscript = useCallback(() => {
    setShowTranscript(false);
    // Wait for transition to finish before removing from DOM
    setTimeout(() => setTranscriptOverlay(false), 300);
  }, []);

  // --- Save indicator label ---
  const saveLabel = useMemo(() => {
    if (saveState === 'saving') return 'Saving...';
    if (saveState === 'error') return 'Save failed';
    if (saveState === 'saved' && lastSavedDisplay) return `Saved ${lastSavedDisplay}`;
    if (isDirty) return 'Unsaved changes';
    return '';
  }, [saveState, isDirty, lastSavedDisplay]);

  const saveLabelColor =
    saveState === 'error'
      ? 'text-safety-red'
      : saveState === 'saving'
        ? 'text-clinical-text-muted'
        : isDirty
          ? 'text-clinical-amber'
          : 'text-clinical-text-muted';

  // --- Render loading / error ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF7' }}>
        <div className="text-center space-y-3">
          <div className="skeleton h-8 w-48 mx-auto rounded" />
          <div className="skeleton h-4 w-32 mx-auto rounded" />
        </div>
      </div>
    );
  }

  if (loadError || !note) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF7' }}>
        <div className="clinical-card p-8 max-w-sm w-full text-center space-y-4">
          <h1 className="font-heading text-xl text-clinical-charcoal">
            Note not found
          </h1>
          <p className="text-sm font-body text-clinical-text-muted">
            {loadError ?? 'This note could not be loaded.'}
          </p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/notes/pending')}
          >
            Back to notes
          </button>
        </div>
      </div>
    );
  }

  const fields: TemplateField[] = note.templateFields ?? [];

  return (
    <>
      {/* Transcript overlay backdrop */}
      {transcriptOverlay && (
        <div
          className={[
            'fixed inset-0 z-40 bg-clinical-charcoal/20 backdrop-blur-sm',
            'transition-opacity duration-300',
            showTranscript ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
          onClick={closeTranscript}
          aria-hidden="true"
        />
      )}

      {/* Transcript slide-in panel */}
      {transcriptOverlay && (
        <div
          style={{
            transform: showTranscript ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 300ms ease-out',
          }}
        >
          <TranscriptPanel segments={segments} onClose={closeTranscript} />
        </div>
      )}

      {/* Full-screen editor layout */}
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: '#FAFAF7' }}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-clinical-border">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
            {/* Left: back + title */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                className="p-2 rounded-lg text-clinical-text-muted hover:text-clinical-charcoal hover:bg-clinical-cream transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <ChevronLeftIcon />
              </button>
              <div className="min-w-0">
                <h1 className="font-heading text-base font-semibold text-clinical-charcoal truncate">
                  {note.templateName}
                </h1>
                <p className="text-xs font-body text-clinical-text-muted truncate">
                  {new Date(note.sessionDate).toLocaleDateString([], {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>

            {/* Center: status badge + save indicator */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {saveLabel && (
                <span className={`text-xs font-body ${saveLabelColor} hidden sm:block`}>
                  {saveLabel}
                </span>
              )}
              {note.status === 'DRAFT' && (
                <span className="status-draft">Draft</span>
              )}
              {note.status === 'COMPLETING' && (
                <span className="status-completing">Completing</span>
              )}
              {note.status === 'COMPLETED' && (
                <span className="status-completed">Completed</span>
              )}
            </div>

            {/* Right: preview toggle + copy-to-EMR + transcript toggle + manual save */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                className="btn-secondary px-3 py-2 text-sm min-h-[40px]"
                onClick={() => setPreviewOn((v) => !v)}
                aria-pressed={previewOn}
                aria-label="Toggle note preview pane"
              >
                {previewOn ? 'Preview: on' : 'Preview: off'}
              </button>
              <button
                type="button"
                className="btn-primary px-3 py-2 text-sm min-h-[40px]"
                onClick={() => note && void copyToEmr(note.id)}
                disabled={copyState === 'copying' || note?.status === 'COMPLETING'}
                aria-label="Copy note to EMR"
              >
                {copyState === 'copying' ? 'Copying...' :
                 copyState === 'success' ? 'Copied ✓' :
                 copyState === 'error' ? 'Copy failed' :
                 'Copy to EMR'}
              </button>
              <button
                type="button"
                className="btn-secondary px-3 py-2 text-sm min-h-[40px] gap-1.5"
                onClick={openTranscript}
                aria-label="View session transcript"
              >
                <DocumentTextIcon />
                <span className="hidden sm:inline">Transcript</span>
              </button>
              <button
                type="button"
                className="btn-primary px-3 py-2 text-sm min-h-[40px]"
                disabled={!isDirty || saveState === 'saving'}
                onClick={() => void performSave()}
              >
                {saveState === 'saving' ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </header>

        {/* Note form (+ optional preview pane when previewOn) */}
        <main className={`flex-1 w-full px-4 sm:px-6 py-8 ${previewOn ? 'mx-auto max-w-6xl' : 'mx-auto max-w-3xl'}`}>
          {/* Mobile save indicator */}
          {saveLabel && (
            <p className={`text-xs font-body ${saveLabelColor} mb-4 sm:hidden`}>
              {saveLabel}
            </p>
          )}

          <div className={previewOn ? 'flex gap-6 items-start' : undefined}>
            {/* Form column */}
            <div className={previewOn ? 'flex-1 min-w-0' : undefined}>
              <div className="clinical-card p-6 sm:p-8 space-y-6">
                {fields.length === 0 ? (
                  <p className="text-sm font-body text-clinical-text-muted italic">
                    No template fields defined for this note.
                  </p>
                ) : (
                  fields.map((field) => (
                    <FieldRenderer
                      key={field.key}
                      field={field}
                      value={content[field.key]}
                      onChange={handleFieldChange}
                    />
                  ))
                )}

                {/* Scroll sentinel — IntersectionObserver watches this */}
                <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />

                {/* Scroll gate + complete button */}
                <ScrollGateButton
                  noteId={note.id}
                  sentinelRef={sentinelRef}
                  onComplete={handleComplete}
                  initialScrolled={note.scrolledToBottom}
                  disabled={saveState === 'saving'}
                />
              </div>
            </div>

            {/* Preview pane (right column when previewOn) */}
            {previewOn && (
              <aside
                className="flex-1 min-w-0 clinical-card p-6 sm:p-8 sticky top-16 overflow-y-auto max-h-[calc(100vh-5rem)]"
                aria-label="Rendered note preview"
              >
                <p className="text-xs font-semibold text-clinical-text-muted uppercase tracking-wide mb-4">
                  Preview
                </p>
                {previewLoading ? (
                  <p className="text-sm font-body text-clinical-text-muted">Loading…</p>
                ) : (
                  <div className="prose prose-sm max-w-none font-body text-clinical-charcoal">
                    <ReactMarkdown>{previewMarkdown}</ReactMarkdown>
                  </div>
                )}
              </aside>
            )}
          </div>
        </main>
      </div>
    </>
  );
}

// --- Inline SVG icons ---

function ChevronLeftIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function DocumentTextIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
